import {
  forwardRef,
  useRef,
  useEffect,
  useState,
  useCallback,
  KeyboardEvent,
} from 'react';
import { cn } from '@antimatterhq/ui/lib/utils';
import { X } from 'lucide-react';
import { Text } from '../typography/typography';
import { EditableChipPipeAddon } from './addon/editableChipPipeAddon';

export interface Rule {
  match: RegExp;
  theme: string;
  validation: (value: string) => string | undefined;
}

export type EditableChipProps = {
  value: string;
  isEditing: boolean;
  editingValue: string;
  rules: Rule[];
  hasBeenCompleted?: boolean;
  debugMode?: boolean;
  isDragging?: boolean;
  disableHover?: boolean;
  initialCursorPosition?: number;
  className?: string;
  onEdit: () => void;
  onChange: (value: string) => void;
  onCommit: (value: string) => void;
  onDelete: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: () => void;
  onDragStart?: (e: React.DragEvent, hoverHTML: HTMLElement) => void;
  onDragEnd?: (e: React.DragEvent) => void;
};

const PIPE_ADDON_WIDTH = 20;

export const EditableChip = forwardRef<HTMLElement, EditableChipProps>(
  (
    {
      value,
      isEditing,
      editingValue,
      rules,
      hasBeenCompleted = false,
      debugMode = false,
      isDragging = false,
      disableHover = false,
      initialCursorPosition,
      className,
      onEdit,
      onChange,
      onDelete,
      onKeyDown,
      onBlur,
      onDragStart,
      onDragEnd,
    },
    ref,
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const chipRef = useRef<HTMLDivElement>(null);
    const hoverRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [matchedRule, setMatchedRule] = useState<Rule | null>(null);
    const [validationError, setValidationError] = useState<
      string | undefined
    >();
    const [debouncedValue, setDebouncedValue] = useState(editingValue);
    const [pendingCursorPosition, setPendingCursorPosition] = useState<
      number | null
    >(null);
    const [chipWidth, setChipWidth] = useState<number | null>(null);

    useEffect(() => {
      if (disableHover) {
        setIsHovered(false);
      }
    }, [disableHover]);

    // Focus textarea when entering edit mode
    useEffect(() => {
      if (isEditing && textareaRef.current) {
        textareaRef.current.focus();

        // Set cursor position if it was calculated from click or passed in
        const cursorPos = pendingCursorPosition ?? initialCursorPosition;
        if (cursorPos !== null && cursorPos !== undefined) {
          textareaRef.current.setSelectionRange(cursorPos, cursorPos);
          setPendingCursorPosition(null);
        }
      }
    }, [isEditing, pendingCursorPosition, initialCursorPosition]);

    // Debounce value changes for rule matching
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedValue(isEditing ? editingValue : value);
      }, 100);

      return () => clearTimeout(timer);
    }, [editingValue, value, isEditing]);

    // Measure chip width when hovered
    useEffect(() => {
      if (isHovered && chipRef.current) {
        const rect = chipRef.current.getBoundingClientRect();
        setChipWidth(rect.width - PIPE_ADDON_WIDTH);
      } else {
        setChipWidth(null);
      }
    }, [isHovered]);

    // Auto-resize textarea based on content
    useEffect(() => {
      if (textareaRef.current && isEditing) {
        // Reset height to auto to get the correct scrollHeight
        textareaRef.current.style.height = 'auto';
        // Set height to scrollHeight to fit content
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }
    }, [editingValue, isEditing]);

    // Match rules and validate
    useEffect(() => {
      const currentValue = debouncedValue;

      // Find matching rule
      const matched = rules.find((rule) => rule.match.test(currentValue));
      setMatchedRule(matched ?? null);

      // Run validation if rule matched
      if (matched) {
        const error = matched.validation(currentValue);
        setValidationError(error);
      } else {
        setValidationError(undefined);
      }
    }, [debouncedValue, rules]);

    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        if (hoverRef.current) {
          onDragStart?.(e, hoverRef.current);
        }
      },
      [onDragStart],
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text');
        // Strip newlines from pasted text
        const cleanedText = text.replace(/[\r\n]+/g, ' ');
        const textarea = e.currentTarget;
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const newValue =
          editingValue.slice(0, start) + cleanedText + editingValue.slice(end);
        onChange(newValue);
      },
      [editingValue, onChange],
    );

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isEditing) {
          // Calculate where in the text the click occurred
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left - 8 - PIPE_ADDON_WIDTH; // Subtract padding (px-2 = 0.5rem = 8px)

          // Find the character position based on click position
          let clickPosition = 0;
          if (value && x > 0) {
            // Create a temporary span to measure character positions
            const tempSpan = document.createElement('span');
            tempSpan.style.visibility = 'hidden';
            tempSpan.style.position = 'absolute';
            tempSpan.style.fontSize = '0.875rem'; // text-sm
            tempSpan.style.fontFamily = window.getComputedStyle(
              e.currentTarget,
            ).fontFamily;
            tempSpan.style.whiteSpace = 'pre';
            document.body.appendChild(tempSpan);

            // Binary search to find the closest character position
            let left = 0;
            let right = value.length;
            while (left < right) {
              const mid = Math.floor((left + right) / 2);
              tempSpan.textContent = value.slice(0, mid);
              const width = tempSpan.getBoundingClientRect().width;

              if (width < x) {
                left = mid + 1;
              } else {
                right = mid;
              }
            }

            // Check if we're closer to the previous or next character
            if (left > 0) {
              tempSpan.textContent = value.slice(0, left - 1);
              const prevWidth = tempSpan.getBoundingClientRect().width;
              tempSpan.textContent = value.slice(0, left);
              const currWidth = tempSpan.getBoundingClientRect().width;

              if (x - prevWidth < currWidth - x) {
                clickPosition = left - 1;
              } else {
                clickPosition = left;
              }
            } else {
              clickPosition = 0;
            }

            document.body.removeChild(tempSpan);
          }

          // Store click position for when entering edit mode
          setPendingCursorPosition(clickPosition);
          onEdit();
        }
      },
      [isEditing, onEdit, value],
    );

    const chipContent = isEditing ? editingValue : value;
    const hasContent = chipContent.length > 0;

    // Determine chip theme/color
    let chipTheme = 'border-gray-300 dark:border-gray-600'; // default
    if (matchedRule) {
      chipTheme = matchedRule.theme;
    }

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={cn(
          'relative inline-block', // Changed to inline-block
          'transition-all duration-200',
          isEditing || hasContent ? 'min-w-[2rem]' : 'min-w-[1rem]',
          isHovered && !isEditing && value ? 'overflow-visible' : '',
          className,
        )}
        onMouseEnter={() => !disableHover && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          ref={chipRef}
          className={cn(
            'relative cursor-text rounded-md border py-1 pr-2',
            hasContent ? '' : 'border-transparent',
            hasContent ? chipTheme : '',
            isDragging && 'opacity-50',
            // Ensure minimum height even when empty and editing
            isEditing && !hasContent && 'min-h-[30px]',
          )}
          onClick={handleClick}
          style={{
            paddingLeft: `${PIPE_ADDON_WIDTH + 8}px`,
          }}
        >
          <EditableChipPipeAddon
            width={PIPE_ADDON_WIDTH}
            className="absolute bottom-0 left-0 top-0"
          />
          <div className="relative">
            {/* Visible text */}
            <Text
              className={cn(
                'block text-sm',
                !isEditing && 'select-none',
                hasBeenCompleted &&
                  validationError &&
                  'underline decoration-red-500 decoration-2 underline-offset-2',
                debugMode && isEditing && 'opacity-50', // Make semi-transparent in debug mode
              )}
              style={{
                fontSize: '0.875rem', // text-sm equivalent
                lineHeight: '1.25rem',
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                boxSizing: 'border-box',
              }}
            >
              {chipContent}
            </Text>

            {/* Textarea overlay - styled to match span but with transparent text */}
            {isEditing && (
              <textarea
                ref={textareaRef}
                value={editingValue}
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                onKeyDown={(e) => {
                  // Prevent Enter from creating newlines
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                  onKeyDown(e);
                }}
                onBlur={onBlur}
                onPaste={handlePaste}
                className={cn(
                  'absolute inset-0',
                  'block',
                  'border-none bg-transparent outline-none',
                  'm-0 p-0',
                  !debugMode && 'text-transparent', // Hide text but keep cursor when not debugging
                  debugMode && 'text-black dark:text-white', // Show text in debug mode
                  'caret-black dark:caret-white', // Style cursor color
                  'resize-none', // Prevent manual resizing
                  'overflow-hidden', // Hide scrollbars
                )}
                style={{
                  fontSize: '0.875rem', // text-sm equivalent
                  lineHeight: '1.25rem',
                  fontFamily: 'inherit',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  boxSizing: 'border-box',
                  // Remove minHeight to allow proper auto-resize
                }}
              />
            )}
          </div>
        </div>

        {/* Hover overlay with delete button */}
        {isHovered && !isEditing && value && (
          <div
            className={cn(
              'absolute left-0 top-0 rounded-md',
              'flex',
              'border',
              matchedRule
                ? matchedRule.theme
                : 'border-gray-400 bg-white dark:border-gray-500 dark:bg-gray-900',
              'shadow-md',
              'z-20',
              isDragging && 'opacity-0',
            )}
            onClick={handleClick}
            ref={hoverRef}
          >
            <EditableChipPipeAddon
              width={PIPE_ADDON_WIDTH}
              onDragStart={handleDragStart}
              onDragEnd={onDragEnd}
            />
            <div
              className="flex-1"
              style={{
                width: chipWidth ? `${chipWidth - 2}px` : 'auto',
                minWidth: chipWidth ? `${chipWidth - 2}px` : 'auto',
              }}
            >
              <Text
                className={cn(
                  'block cursor-text select-none px-2 py-1 text-sm',
                  hasBeenCompleted &&
                    validationError &&
                    'underline decoration-red-500 decoration-2 underline-offset-2',
                )}
                style={{
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {value}
              </Text>
            </div>
            <div className="flex items-center">
              <div className="h-full w-[1px] bg-current opacity-20" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className={cn(
                  'flex items-center rounded-r px-1.5 py-1',
                  'group',
                )}
              >
                <X className="h-3 w-3 text-gray-500 transition-colors group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  },
);

EditableChip.displayName = 'EditableChip';
